"use client"

import { useCallback, useEffect, useRef } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { reaction } from "mobx"

import { ApiService } from "@/lib/api"
import { useStore } from "@/app/components/common/store.provider"
import type { CartServiceItem } from "@/lib/types/cart.types"
import type { Service } from "@/lib/types/service.types"
import type { BookingMode, PaymentMethod } from "@/store/appointment-store"

const BOOKING_PARAM_KEYS = [
  "book",
  "mode",
  "step",
  "services",
  "date",
  "time",
  "payment",
  "custom",
  "notify",
] as const

const HYDRATION_SIGNATURE_KEYS = [
  "mode",
  "services",
  "step",
  "date",
  "time",
  "custom",
  "payment",
  "notify",
] as const

export const BOOKING_HOST_PATHS = ["/", "/customer/dashboard"] as const

export type OpenAppointmentBookingOptions = {
  serviceIds?: number[]
  serviceSlugs?: string[]
  step?: number
  /** Omit to land on the "how do you want to book" screen. */
  mode?: BookingMode
}

export function isBookingHostPath(pathname: string): boolean {
  if ((BOOKING_HOST_PATHS as readonly string[]).includes(pathname)) return true
  return pathname === "/services" || pathname.startsWith("/services/")
}

export function buildAppointmentBookingUrl(
  pathname: string,
  opts?: OpenAppointmentBookingOptions
): string {
  const params = new URLSearchParams()
  params.set("book", "1")
  params.set("step", String(opts?.step ?? 1))

  const serviceRefs: string[] = []
  if (opts?.serviceIds?.length) {
    serviceRefs.push(...opts.serviceIds.map(String))
  }
  if (opts?.serviceSlugs?.length) {
    serviceRefs.push(...opts.serviceSlugs)
  }
  if (serviceRefs.length) {
    params.set("services", serviceRefs.join(","))
  }

  // Preselecting services only makes sense for the single-guest flow, so those
  // links skip the mode picker.
  const mode = opts?.mode ?? (serviceRefs.length ? "single" : undefined)
  if (mode) params.set("mode", mode)

  return `${pathname}?${params.toString()}`
}

function buildHydrationSignature(params: URLSearchParams): string {
  const parts: string[] = []
  for (const key of HYDRATION_SIGNATURE_KEYS) {
    parts.push(`${key}=${params.get(key) ?? ""}`)
  }
  return parts.join("&")
}

function toCartService(service: Service): CartServiceItem {
  return {
    id: Number(service.id),
    name: service.service_name,
    description: service.service_description,
    price: service.service_price,
    duration: service.service_duration,
    targetAudience: service.target_audience,
  }
}

function parseServiceRefs(raw: string | null): Array<number | string> {
  if (!raw) return []
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const asNumber = Number.parseInt(part, 10)
      if (Number.isFinite(asNumber) && asNumber > 0 && String(asNumber) === part) {
        return asNumber
      }
      return part
    })
}

function parseStep(raw: string | null, maxSteps = 4): number {
  const step = Number.parseInt(raw ?? "1", 10)
  if (!Number.isFinite(step)) return 1
  return Math.min(maxSteps, Math.max(1, step))
}

function parsePayment(raw: string | null): PaymentMethod {
  if (raw === "Transfer" || raw === "Card") return raw
  return "Cash"
}

function parseMode(params: URLSearchParams): BookingMode | null {
  const raw = params.get("mode")
  if (raw === "group" || raw === "single") return raw
  // Legacy links carry booking detail without a mode; those are single bookings.
  if (params.get("services") || params.get("date") || params.get("time")) {
    return "single"
  }
  return null
}

function resolveStepAfterHydrate(
  requestedStep: number,
  hasServices: boolean,
  isCustom: boolean,
  hasDateTime: boolean
): number {
  let step = requestedStep
  if (step >= 2 && !hasServices) step = 1
  if (step === 3 && !isCustom && !hasDateTime) step = 2
  return step
}

function buildBookingQuery(
  pathname: string,
  store: {
    bookingMode: BookingMode | null
    currentStep: number
    selectedServices: CartServiceItem[]
    selectedDate: string
    selectedSlot: { startTime: string } | null
    selectedPaymentMethod: PaymentMethod
    isCustomBooking: boolean
    notificationsConsent: boolean
  }
): string {
  const params = new URLSearchParams()
  params.set("book", "1")
  if (store.bookingMode) params.set("mode", store.bookingMode)

  params.set("step", String(store.currentStep))

  // Group drafts hold per-guest state that a query string cannot round-trip, so
  // only step and mode are mirrored into the URL for group bookings.
  if (store.bookingMode !== "single") {
    return `${pathname}?${params.toString()}`
  }

  if (store.selectedServices.length > 0) {
    params.set("services", store.selectedServices.map((s) => s.id).join(","))
  }

  if (store.currentStep >= 2) {
    if (store.selectedDate) params.set("date", store.selectedDate)
    if (store.selectedSlot?.startTime) params.set("time", store.selectedSlot.startTime)
  }

  if (store.currentStep >= 3) {
    params.set("payment", store.selectedPaymentMethod)
    params.set("notify", store.notificationsConsent ? "1" : "0")
  }

  if (store.isCustomBooking) params.set("custom", "1")

  return `${pathname}?${params.toString()}`
}

function stripBookingParams(searchParams: URLSearchParams): string {
  const next = new URLSearchParams(searchParams.toString())
  for (const key of BOOKING_PARAM_KEYS) {
    next.delete(key)
  }
  const query = next.toString()
  return query ? `?${query}` : ""
}

async function fetchCartServicesByRefs(
  refs: Array<number | string>
): Promise<CartServiceItem[]> {
  if (!refs.length) return []

  const results = await Promise.all(
    refs.map(async (ref) => {
      try {
        const service =
          typeof ref === "number"
            ? await ApiService.getService(ref)
            : await ApiService.getServiceBySlug(ref)
        return toCartService(service)
      } catch {
        return null
      }
    })
  )

  return results.filter((item): item is CartServiceItem => Boolean(item))
}

function stepFromUrlBeforeServicesLoaded(
  requestedStep: number,
  isCustom: boolean,
  hasDateTime: boolean
): number {
  let step = requestedStep
  if (step === 3 && !isCustom && !hasDateTime) step = 2
  return step
}

function applyParamsToStore(
  params: URLSearchParams,
  appointmentStore: {
    bookingMode: BookingMode | null
    setBookingMode: (mode: BookingMode | null) => void
    setIsCustomBooking: (value: boolean) => void
    setSelectedDate: (date: string) => void
    setSelectedSlot: (slot: { startTime: string } | null) => void
    setSelectedPaymentMethod: (method: PaymentMethod) => void
    setNotificationsConsent: (value: boolean) => void
    initNotificationsConsentFromUser: (value?: boolean) => void
    setCurrentStep: (step: number, force?: boolean) => void
  },
  authNotificationsConsent?: boolean
) {
  const mode = parseMode(params)
  const isCustom = params.get("custom") === "1"
  const date = params.get("date") ?? ""
  const time = params.get("time") ?? ""
  const notifyRaw = params.get("notify")
  const maxSteps = mode === "group" ? 4 : 3
  const requestedStep = parseStep(params.get("step"), maxSteps)
  const hasDateTime = Boolean(date && time)

  appointmentStore.setBookingMode(mode)
  appointmentStore.setIsCustomBooking(isCustom)
  if (date) appointmentStore.setSelectedDate(date)
  if (time) appointmentStore.setSelectedSlot({ startTime: time })
  appointmentStore.setSelectedPaymentMethod(parsePayment(params.get("payment")))

  if (notifyRaw === "0" || notifyRaw === "1") {
    appointmentStore.setNotificationsConsent(notifyRaw === "1")
  } else {
    appointmentStore.initNotificationsConsentFromUser(authNotificationsConsent)
  }

  if (mode) {
    if (mode === "single") {
      appointmentStore.setCurrentStep(
        stepFromUrlBeforeServicesLoaded(requestedStep, isCustom, hasDateTime),
        true
      )
    } else {
      appointmentStore.setCurrentStep(requestedStep, true)
    }
  }
}

export function useOpenAppointmentBooking() {
  const router = useRouter()
  const pathname = usePathname()

  return useCallback(
    (opts?: OpenAppointmentBookingOptions) => {
      if (isBookingHostPath(pathname)) {
        router.replace(buildAppointmentBookingUrl(pathname, opts), { scroll: false })
        return
      }
      router.push(buildAppointmentBookingUrl("/", opts), { scroll: false })
    },
    [pathname, router]
  )
}

export function useAppointmentBookingUrl(options: {
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const { open, setOpen } = options
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { appointmentStore, authStore } = useStore()
  const hydratingRef = useRef(false)
  const hydratedRef = useRef(false)
  const lastHydratedSignatureRef = useRef("")
  const urlSyncReadyRef = useRef(false)
  const wasOpenRef = useRef(false)
  const hydrateSessionRef = useRef(0)

  const clearBookingFromUrl = useCallback(() => {
    const suffix = stripBookingParams(new URLSearchParams(searchParams.toString()))
    router.replace(`${pathname}${suffix}`, { scroll: false })
  }, [pathname, router, searchParams])

  useEffect(() => {
    if (searchParams.get("book") !== "1") return

    const paramsSnapshot = new URLSearchParams(searchParams.toString())
    const signature = buildHydrationSignature(paramsSnapshot)
    if (hydratedRef.current && lastHydratedSignatureRef.current === signature) return

    const session = ++hydrateSessionRef.current
    let cancelled = false

    const hydrate = async () => {
      hydratingRef.current = true
      urlSyncReadyRef.current = false
      hydratedRef.current = false
      appointmentStore.setIsBookingHydrating(true)
      setOpen(true)

      const serviceRefs = parseServiceRefs(paramsSnapshot.get("services"))
      const isCustom = paramsSnapshot.get("custom") === "1"
      const date = paramsSnapshot.get("date") ?? ""
      const time = paramsSnapshot.get("time") ?? ""
      const mode = parseMode(paramsSnapshot)
      const maxSteps = mode === "group" ? 4 : 3
      const requestedStep = parseStep(paramsSnapshot.get("step"), maxSteps)
      const hasDateTime = Boolean(date && time)

      applyParamsToStore(paramsSnapshot, appointmentStore, authStore.user?.notificationsConsent)

      try {
        if (serviceRefs.length > 0) {
          const cartItems = await fetchCartServicesByRefs(serviceRefs)
          if (cancelled || session !== hydrateSessionRef.current) return

          appointmentStore.setSelectedServices(cartItems)

          const step = resolveStepAfterHydrate(
            requestedStep,
            cartItems.length > 0,
            isCustom,
            hasDateTime
          )
          appointmentStore.setCurrentStep(step, true)
        }
        hydratedRef.current = true
        lastHydratedSignatureRef.current = signature
      } finally {
        if (session !== hydrateSessionRef.current) return
        hydratingRef.current = false
        appointmentStore.setIsBookingHydrating(false)
        urlSyncReadyRef.current = true
      }
    }

    void hydrate()
    return () => {
      cancelled = true
      if (session === hydrateSessionRef.current) {
        hydratingRef.current = false
        appointmentStore.setIsBookingHydrating(false)
      }
    }
  }, [
    appointmentStore,
    authStore.user?.notificationsConsent,
    searchParams,
    setOpen,
  ])

  useEffect(() => {
    const dispose = reaction(
      () => ({
        mode: appointmentStore.bookingMode,
        step: appointmentStore.currentStep,
        services: appointmentStore.selectedServices.map((s) => s.id).join(","),
        date: appointmentStore.selectedDate,
        time: appointmentStore.selectedSlot?.startTime ?? "",
        payment: appointmentStore.selectedPaymentMethod,
        custom: appointmentStore.isCustomBooking,
        notify: appointmentStore.notificationsConsent,
      }),
      () => {
        if (!open || hydratingRef.current || !urlSyncReadyRef.current) return
        const nextUrl = buildBookingQuery(pathname, appointmentStore)
        const current = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
        if (current !== nextUrl) {
          const nextSearchParams = new URLSearchParams(nextUrl.split("?")[1] ?? "")
          lastHydratedSignatureRef.current = buildHydrationSignature(nextSearchParams)
          hydratedRef.current = true
          router.replace(nextUrl, { scroll: false })
        }
      }
    )
    return () => dispose()
  }, [appointmentStore, open, pathname, router, searchParams])

  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open
    if (wasOpen && !open) {
      hydratedRef.current = false
      lastHydratedSignatureRef.current = ""
      urlSyncReadyRef.current = false
    }
  }, [open])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (!nextOpen) {
        appointmentStore.reset()
        clearBookingFromUrl()
        hydratedRef.current = false
        lastHydratedSignatureRef.current = ""
        urlSyncReadyRef.current = false
        hydrateSessionRef.current += 1
      }
    },
    [appointmentStore, clearBookingFromUrl, setOpen]
  )

  return { handleOpenChange, clearBookingFromUrl }
}
